import { v4 as uuidv4 } from "uuid";
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { hasPermission } from "../../components/Permissions";
import DraftStatus from "../../constants/draftStatus";
import Permissions from "../../constants/permissions";
import useAuth from "../../context/UserContext";
import { CompilationMessage, CompilationResult } from "../../graphql/types";
import type { File, QueryCompilationResult } from "./types";

export enum EditorTabVariant {
  Editor = "editor",
  EditableDiff = "editable-diff",
  ReadonlyDiff = "readonly-diff",
}

export type OpenFile = {
  id: string;
  path: string;
  originalPath?: string;
  variant: EditorTabVariant;
  type: "file";
};

export type EditorProps = {
  id: string;
  files: Array<File>;
  openFiles: Array<OpenFile>;
  currentOpenFile: OpenFile | null;
  compilationResult: Record<string, CompilationResult>;
  status: DraftStatus;
  addNewFile?: boolean;
};

const binarySearchInsert = (arr: Array<File>, target: File) => {
  let left = 0;
  let right = arr.length - 1;
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);

    if (arr[mid].path === target.path) {
      // If the target is already in the array, insert it at the current position.
      return mid;
    } else if (arr[mid].path < target.path) {
      // If the middle element is less than the target, search the right half.
      left = mid + 1;
    } else {
      // If the middle element is greater than the target, search the left half.
      right = mid - 1;
    }
  }

  // If the target is not found, 'left' will be the correct position for insertion.
  return left;
};

export type EditorState = {
  addFile: (path: string) => void;
  renameFile: (oldPath: string, newPath: string) => void;
  updateCompilationResult: (compilationResult: QueryCompilationResult) => void;
  deleteFile: (path: string) => void;
  setFileText: (path: string, text: string, actor?: "client" | "server", type?: "file") => void;
  resetState: (state: EditorProps) => void;
  openFileTab: (path: string, type: "file", originalPath?: string, variant?: EditorTabVariant) => void;
  updateOpenedFilesPaths: () => void;
  closeFileTab: (id: string) => void;
  setCurrentFile: (file: OpenFile) => void;
  clearClientFileDeletions: () => void;
  resetClientFileDeletions: () => void;
  setStatus: (status: DraftStatus) => void;
  setAddNewFile: (value: boolean) => void;
} & EditorProps;

export const createEditorStore = (initProps: EditorProps) => {
  return createStore<EditorState>()(
    immer<EditorState>((set) => ({
      ...initProps,

      addFile: (path) =>
        set((state) => {
          const newFile: File = {
            __typename: "File",
            id: `('${state.id}', '${path}')`,
            path,
            text: "// An empty script.",
            editFlags: {
              renamed: false,
              created: true,
              updated: false,
              deleted: false,
            },
          };
          const newOpenFile: OpenFile = {
            id: uuidv4(),
            path,
            variant: EditorTabVariant.Editor,
            type: "file",
          };
          const insertPosition = binarySearchInsert(state.files, newFile);
          state.files.splice(insertPosition, 0, newFile);
          state.openFiles.push(newOpenFile);
          state.currentOpenFile = newOpenFile;
        }),

      renameFile: (oldPath, newPath) =>
        set((state) => {
          if (oldPath !== newPath) {
            state.files = state.files.map((f: File) => {
              if (!f || f.path !== oldPath) return f;
              const renamedFile = { ...f, path: newPath };
              if (!renamedFile.editFlags?.created) {
                renamedFile.editFlags = {
                  updated: renamedFile.editFlags?.updated || false,
                  created: renamedFile.editFlags?.created || false,
                  deleted: renamedFile.editFlags?.deleted || false,
                  originalPath: renamedFile.editFlags?.originalPath || oldPath,
                  renamed: true,
                };
              }

              return renamedFile;
            });
            state.files.sort((a, b) => a.path.localeCompare(b.path));
            if (state.compilationResult[oldPath]) {
              state.compilationResult[newPath] = state.compilationResult[oldPath];
              delete state.compilationResult[oldPath];
            }
            if (state.openFiles.some((file) => file.path === oldPath)) {
              state.openFiles = state.openFiles.map((file) =>
                file.path === oldPath
                  ? {
                      path: newPath,
                      type: file.type,
                      variant: file.variant,
                      id: file.id,
                      originalPath: file.originalPath || oldPath, // If the original path was set, it means that this is a second rename.
                    }
                  : file
              );
            }

            if (state.currentOpenFile?.path === oldPath) {
              state.currentOpenFile.path = newPath;
            }
          }
        }),

      updateCompilationResult: (compilationResult) =>
        set((state) => {
          const filesMap = state.files.reduce(
            (val, file) => {
              const result = val[file.path] || {
                __typename: "CompilationResult",
                messages: [],
                declarations: [],
              };

              result.declarations =
                compilationResult?.declarations?.filter((declaration) => declaration?.path === file.path) || [];
              result.messages = compilationResult?.messages?.filter((message) => message?.path === file.path) || [];

              val[file.path] = result;
              return val;
            },
            {} as Record<string, CompilationResult>
          );

          return { compilationResult: filesMap };
        }),

      deleteFile: (path) =>
        set((state) => {
          state.files = state.files.map((file) => {
            if (!file || file.path !== path) return file;

            if (file.editFlags) {
              file.editFlags.deleted = true;
              file.editFlags.originalPath = file.path;
            } else {
              file.editFlags = {
                renamed: false,
                updated: false,
                created: false,
                deleted: true,
                originalPath: file.path, // Store original path in case of rename and delete.
              };
            }

            return file;
          });
          state.openFiles = state.openFiles.filter((f) => f.path !== path);
          if (state.currentOpenFile?.path !== path) return;

          state.currentOpenFile = state.openFiles.length ? state.openFiles[state.openFiles.length - 1] : null;
        }),

      setFileText: (path, text, actor = "client") =>
        set((state) => {
          state.files = state.files.map((f) => {
            if (!f || (f.path !== path && f.editFlags?.originalPath !== path)) return f;
            if (f.editFlags) {
              f.editFlags.updated = actor === "client";
            } else {
              f.editFlags = {
                renamed: false,
                updated: actor === "client",
                created: false,
                deleted: false,
              };
            }
            f.text = text;

            return f;
          });
        }),

      resetState: (state) => set(() => state, true),

      openFileTab: (path, type = "file", originalPath = path, variant = EditorTabVariant.Editor) =>
        set((state) => {
          const existingFile = state.openFiles.find(
            (f) => (f.path === path || f.originalPath === originalPath) && f.variant === variant
          );

          if (existingFile) {
            return {
              currentOpenFile: existingFile,
            };
          } else {
            const newFile: OpenFile = {
              path,
              variant,
              id: uuidv4(),
              originalPath,
              type,
            };

            return {
              openFiles: [...state.openFiles, newFile],
              currentOpenFile: newFile,
            };
          }
        }),
      // We need to update the originalPath whith the new path when we rename a file.
      updateOpenedFilesPaths: () =>
        set((state) => ({
          openFiles: state.openFiles.map((f) => ({
            ...f,
            originalPath: f.path,
          })),
          currentOpenFile: {
            ...state.currentOpenFile,
            originalPath: state.currentOpenFile?.path,
          },
        })),

      closeFileTab: (id) =>
        set((state) => ({
          openFiles: state.openFiles.filter((f) => f.id !== id),
          currentOpenFile:
            state.currentOpenFile?.id === id ? state.openFiles.find((f) => f.id !== id) : state.currentOpenFile,
        })),

      setCurrentFile: (file: OpenFile) => set(() => ({ currentOpenFile: file })),

      setStatus: (status) => set(() => ({ status })),

      setAddNewFile: (value) => set(() => ({ addNewFile: value })),

      clearClientFileDeletions: () =>
        set((state) => ({
          files: state.files.filter((f) => !(f.editFlags?.deleted && f.editFlags.created)),
        })),
      resetClientFileDeletions: () =>
        set((state) => {
          const files: typeof state.files = [];
          state.files.forEach((f) => {
            if (!f.editFlags?.deleted) {
              files.push({
                ...f,
                editFlags: {
                  renamed: false,
                  updated: false,
                  created: false,
                  deleted: false,
                },
              });
            }
          });
          return { files };
        }),
    }))
  );
};

// Selectors
export const selectAllDeclarations = (state: EditorState) =>
  Object.values(state.compilationResult)
    .map((res) => res.declarations)
    .reduce((declarations, newDecls) => newDecls && declarations?.concat(newDecls), []);

export const selectTestableExportDeclsForFile = (fileName: string) => (state: EditorState) => {
  const compilationResult = state.compilationResult[fileName];
  const filteredExports = compilationResult?.declarations?.filter((f) => {
    return f.test && f.declaration_type === "export";
  });
  if (filteredExports?.length == 0) return null;
  const result = filteredExports?.map((exportItem) => exportItem.name);
  result?.sort((s1: string, s2: string) => {
    return s1.toLowerCase() > s2.toLowerCase() ? 1 : -1;
  });
  return result;
};

export const selectTestableDeclarations = (state: EditorState) =>
  Object.values(state.compilationResult)
    .map((res) => res.declarations)
    .reduce((declarations, newDecls) => newDecls && declarations?.concat(newDecls), [])
    ?.filter((decl) => decl.test)
    .map((decl) => decl.name) || [];

export const selectErrorsAndWarnings = (fileName: string) => (state: EditorState) => {
  const compilationResult = state.compilationResult[fileName];
  const messages = compilationResult?.messages;

  const { user } = useAuth();
  const canSeeBetaMessages = hasPermission(Permissions.BetaMessages, user);

  let errors = 0;
  let warnings = 0;
  let beta = 0;

  messages?.forEach((message) => {
    if (message.type === "error") {
      errors++;
    }
    if (message.type === "warning") {
      warnings++;
    }
    if (message.type === "beta" && canSeeBetaMessages) {
      beta++;
    }
  });
  return [errors, warnings, beta];
};

export const selectErrorsAndWarningsFromAllFiles = () => (state: EditorState) => {
  const messages: CompilationMessage[] = [];

  for (const key in state.compilationResult) {
    state.compilationResult[key].messages?.forEach((message) => {
      messages?.push(message);
    });
  }

  let errors = 0;
  let warnings = 0;
  let beta = 0;

  messages?.forEach((message) => {
    if (message.type === "error") {
      errors++;
    }
    if (message.type === "warning") {
      warnings++;
    }
    if (message.type === "beta") {
      beta++;
    }
  });
  return [errors, warnings, beta];
};

// Util
export const buildCompilationResultMap = (files: Omit<File, "text">[], compilationResult: CompilationResult) => {
  return files.reduce(
    (val, file: File) => {
      const result = val[file.path] || {
        __typename: "CompilationResult",
        messages: [],
        declarations: [],
      };

      result.declarations =
        compilationResult?.declarations?.filter((declaration) => declaration?.path === file.path) || [];
      result.messages = compilationResult?.messages?.filter((message) => message?.path === file.path) || [];

      val[file.path] = result;
      return val;
    },
    {} as Record<string, CompilationResult>
  ) as Record<string, CompilationResult>;
};

export type EditorStore = ReturnType<typeof createEditorStore>;
